import { z } from "zod";

import { assertEditorSettingString, EditorSchema, emptyEditorDoc } from "~/utils/types/rich-text";

/**
 * Shared custom header/footer blocks for always-on order PDFs (packing slip, warehouse release v3).
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md § Packing List & Warehouse Release (always-on — SC-2489)
 */
export const DocumentCustomTextBoilerplateSchema = z.object({
  customTextHeader: EditorSchema.default(emptyEditorDoc()),
  customTextFooter: EditorSchema.default(emptyEditorDoc()),
  customTextHeaderEnabled: z.boolean().default(false),
  customTextFooterEnabled: z.boolean().default(false),
});

export const DocumentCustomTextModalSlotsSchema = z.object({
  customTextHeader: z.boolean().default(false),
  customTextFooter: z.boolean().default(false),
});

const optionalPdfText = z.string().trim().max(8000).optional().default("");

/** Per-create modal overrides — not persisted on the order. */
export const DocumentCustomTextPdfInputSchema = z.object({
  customTextHeader: optionalPdfText,
  customTextFooter: optionalPdfText,
});

export type DocumentCustomTextBoilerplate = z.infer<typeof DocumentCustomTextBoilerplateSchema>;
export type DocumentCustomTextModalSlots = z.infer<typeof DocumentCustomTextModalSlotsSchema>;
export type DocumentCustomTextPdfInput = z.infer<typeof DocumentCustomTextPdfInputSchema>;

export const emptyDocumentCustomTextBoilerplate = (): DocumentCustomTextBoilerplate => ({
  customTextHeader: emptyEditorDoc(),
  customTextFooter: emptyEditorDoc(),
  customTextHeaderEnabled: false,
  customTextFooterEnabled: false,
});

export const emptyDocumentCustomTextModalSlots = (): DocumentCustomTextModalSlots => ({
  customTextHeader: false,
  customTextFooter: false,
});

export const emptyDocumentCustomTextPdfInput = (): DocumentCustomTextPdfInput => ({
  customTextHeader: "",
  customTextFooter: "",
});

/** Rich-text editor JSON sanity check for org settings writes (super-admin + settings UI). */
export const assertDocumentCustomTextBoilerplateEditorSettings = (boilerplate: DocumentCustomTextBoilerplate): void => {
  assertEditorSettingString(JSON.stringify(boilerplate.customTextHeader));
  assertEditorSettingString(JSON.stringify(boilerplate.customTextFooter));
};
